/**
 * "Keeping your data safe" in Settings: persistence status, backup export,
 * and restore. Deliberately honest about what each measure does and does
 * not protect against.
 */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import { useActions } from '../lib/store';
import { buildBackup, downloadBackup, parseBackup, BackupParseError } from '../lib/backup';
import { requestPersistence, storageStatus, type StorageStatus } from '../lib/install';
import { formatDate } from '../lib/time';
import { useLocale } from '../i18n/i18n';

type Busy = 'none' | 'exporting' | 'importing';

export default function DataSafetySection() {
  const t = useT();
  const locale = useLocale();
  const { importBackup } = useActions();
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [busy, setBusy] = useState<Busy>('none');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void storageStatus().then(setStatus);
  }, []);

  const onProtect = async () => {
    setError(null);
    const next = await requestPersistence();
    setStatus(next);
    if (!next.persisted) setMessage(t.dataSafety.protectDeclined);
  };

  const onExport = async () => {
    setBusy('exporting');
    setError(null);
    setMessage(null);
    try {
      const backup = await buildBackup();
      const how = await downloadBackup(backup);
      setMessage(
        how === 'shared'
          ? t.dataSafety.exportShared(backup.events.length)
          : t.dataSafety.exportDownloaded(backup.events.length),
      );
    } catch {
      setError(t.dataSafety.exportFailed);
    } finally {
      setBusy('none');
    }
  };

  const onFile = async (file: File) => {
    setBusy('importing');
    setError(null);
    setMessage(null);
    try {
      const backup = parseBackup(await file.text());
      const result = await importBackup(backup);
      setMessage(
        result.added === 0 && result.updated === 0
          ? t.dataSafety.importNothingNew
          : t.dataSafety.importDone(result.added, result.updated),
      );
    } catch (err) {
      setError(
        err instanceof BackupParseError ? t.dataSafety.importBadFile : t.dataSafety.importFailed,
      );
    } finally {
      setBusy('none');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const persisted = status?.persisted === true;

  return (
    <section className="card space-y-3 p-4">
      <div>
        <h2 className="text-base">{t.dataSafety.title}</h2>
        <p className="mt-1 text-sm text-ink-soft">{t.dataSafety.intro}</p>
      </div>

      {status?.supported ? (
        <div className="rounded-xl bg-surface-2 p-3">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${persisted ? 'bg-good' : 'bg-warn'}`}
            />
            <span className="text-sm font-bold">
              {persisted ? t.dataSafety.protectedOn : t.dataSafety.protectedOff}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-soft">
            {persisted ? t.dataSafety.protectedOnBody : t.dataSafety.protectedOffBody}
          </p>
          {!persisted ? (
            <>
              {/* Warn before the browser's own dialog appears: it cannot be
                  reworded, so the only way to make it understandable is to
                  say what it will ask and what to answer. */}
              <p className="mt-2 text-sm text-accent">{t.persist.whatHappensNext}</p>
              <button
                type="button"
                onClick={() => void onProtect()}
                className="btn-secondary mt-2 min-h-[44px] w-full px-4"
              >
                {t.dataSafety.protectButton}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => void onExport()}
          disabled={busy !== 'none'}
          className="btn-primary min-h-[44px] w-full px-4 disabled:opacity-60"
        >
          {busy === 'exporting' ? t.dataSafety.exporting : t.dataSafety.exportButton}
        </button>
        <p className="text-sm text-ink-soft">{t.dataSafety.exportHint}</p>

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy !== 'none'}
          className="btn-secondary min-h-[44px] w-full px-4 disabled:opacity-60"
        >
          {busy === 'importing' ? t.dataSafety.importing : t.dataSafety.importButton}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
          }}
        />
        <p className="text-sm text-ink-soft">{t.dataSafety.importHint}</p>
      </div>

      {message ? (
        <p role="status" className="rounded-xl bg-good-soft p-3 text-sm text-good">
          {message}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-xl bg-bad-soft p-3 text-sm text-bad">
          {error}
        </p>
      ) : null}

      <p className="text-sm text-ink-faint">
        {t.dataSafety.clearWarning}
        {status?.usageBytes !== undefined ? (
          <>
            {' '}
            {t.dataSafety.usage(formatBytes(status.usageBytes), formatDate(new Date().toISOString(), locale))}
          </>
        ) : null}
      </p>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
